import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { listHrContracts, listHrPeople, listPayrollMovements } from '../../api/hr';
import { getSocialChargesPlanPeople } from '../../api/socialChargesPlan';
import { eptForMonth } from '../treasury/engine/computePayrollFromHr';
import { formatMoney } from '../../utils/format';
import { normalizeName, personIsActive, buildSearchKey } from '../../utils/personTable';
import PageHeader from '../../components/PageHeader';
import CardShell from '../../components/CardShell';
import SearchField from '../../components/SearchField';
import SegmentedToggle from '../../components/SegmentedToggle';
import FilterIcon from '../../components/FilterIcon';
import FilterCount from '../../components/FilterCount';
import './HrPage.css';
import './socialChargesGrid.css';

/* People → Plan masse salariale.
   Détail du brut planifié (contrats + mouvements) sur 24 mois (N + N+1), charges
   salarié → salaire NET, charges employeur → COÛT EMPLOYEUR, sous-total charges.
   Donnée confidentielle (salaires) → vit dans People. Le phasing des décaissements
   et la synthèse cash vivent dans Finance → Prévision trésorerie (mêmes calculs, moteur partagé). */

export default function PayrollMassPlanPage({ activeTenant, year, onYearChange, embedded, unit = 'chf' }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const N = year || new Date().getFullYear();
  const chfLbl = unit === 'kchf' ? 'kCHF' : 'CHF';

  const [contracts, setContracts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    Promise.all([
      listHrContracts(tenantId),
      listPayrollMovements(tenantId),
      listHrPeople(tenantId).catch(() => ({ data: [] })),
    ])
      .then(([c, m, pl]) => {
        if (!c.error) setContracts(c.data || []);
        if (!m.error) setMovements(m.data || []);
        if (!pl.error) setPeople(pl.data || []);
      })
      .finally(() => setLoading(false));
  }, [tenantId, N]);

  // Filtre standard (PersonFilterBar) : recherche personne / département +
  // toggle Actifs / Tous. La recherche sélectionne des PERSONNES (tous leurs
  // contrats suivent) ; les mouvements synthétiques (sans person_id) sortent
  // du périmètre dès qu'un filtre est actif.
  const q = normalizeName(search.trim());
  const hasFilter = statusFilter === 'active' || q !== '';
  const keptIds = useMemo(() => {
    if (!hasFilter) return null;
    const byPerson = new Map();
    contracts.forEach((c) => {
      if (!c.person_id) return;
      if (!byPerson.has(c.person_id)) byPerson.set(c.person_id, []);
      byPerson.get(c.person_id).push(c);
    });
    const ids = new Set();
    people.forEach((per) => {
      const cs = byPerson.get(per.id) || [];
      if (statusFilter === 'active' && !personIsActive(cs)) return;
      if (q && !buildSearchKey(per.first_name, per.last_name, ...cs.map((c) => c.department)).includes(q)) return;
      ids.add(per.id);
    });
    return ids;
  }, [hasFilter, contracts, people, statusFilter, q]);

  const filtered = useMemo(() => {
    if (!keptIds) return { contracts, movements };
    const fc = contracts.filter((c) => c.person_id && keptIds.has(c.person_id));
    const fm = movements.filter((mv) => mv.person_id && keptIds.has(mv.person_id));
    return { contracts: fc, movements: fm };
  }, [contracts, movements, keptIds]);

  /* Plan CALCULÉ PAR LE SERVEUR (moteur migré du JavaScript le 2026-08-21) :
     le filtre de personnes part en paramètre, le serveur calcule, la page LIT. */
  const personKey = keptIds ? [...keptIds].sort().join(',') : '';
  const [plan, setPlan] = useState(null);
  /* Une lecture refusée (client sans People) ne laisse plus l'écran en
     « Chargement… » pour toujours (2026-09-28). */
  const [failed, setFailed] = useState(false);
  /* Le second appel (`movements=0`) est parti avec le graphique de variation
     (2026-08-26) : il ne servait qu'à sa série de référence. Cet écran lit une
     seule série, mouvements compris. */
  useEffect(() => {
    if (!tenantId) return;
    setFailed(false);
    getSocialChargesPlanPeople(tenantId, N, keptIds ? [...keptIds] : null)
      .then((r) => { setPlan(r.error ? null : r.data); setFailed(Boolean(r.error)); })
      .catch(() => { setPlan(null); setFailed(true); });
  }, [tenantId, N, personKey]);  // eslint-disable-line

  /* EPT par mois — calculé AVANT les retours anticipés ci-dessous. Un hook
     placé après un `return` fait varier le NOMBRE de hooks d'un rendu à
     l'autre : au premier passage `plan` est null (on sort tôt, le hook n'est
     jamais appelé), au suivant il existe et le hook s'ajoute — React lève
     alors « Rendered more hooks than during the previous render » et l'écran
     casse. Le calcul tolère donc `plan` absent. */
  const ept = useMemo(() => {
    const ms = plan ? plan.months.slice(0, 12) : [];
    return ms.map((m) => eptForMonth({
      contracts: filtered.contracts, movements: filtered.movements,
      year: m.year, monthIdx: m.monthIdx,
    }));
  }, [plan, filtered]);

  if (!tenantId) return <main className="hr-page"><div className="hr-page__empty">Select a tenant first.</div></main>;
  if (!plan) return <main className="hr-page"><div className="hr-page__empty">{failed ? p('Données de People indisponibles.') : p('Chargement…')}</div></main>;

  // Une année à la fois (pilotée par le sélecteur Year global) : 12 premiers mois.
  const S = (a) => a.slice(0, 12);
  const months = plan.months.slice(0, 12);
  const gross = S(plan.gross);
  const net = S(plan.net);
  const cout = S(plan.cout);
  const sub = S(plan.sub);
  // Sous-totaux dérivés : charges salarié = brut − net ; charges employeur = coût − brut.
  const chargesSalarie = gross.map((g, i) => g - net[i]);
  const chargesEmployeur = cout.map((c, i) => c - gross[i]);
  const charges = plan.charges.map((c) => {
    const empl = S(c.empl);
    const empr = S(c.empr);
    return {
      ...c, empl, empr,
      hasEmpl: empl.some((v) => Math.round(v) !== 0),
      hasEmpr: empr.some((v) => Math.round(v) !== 0),
    };
  });

  const coutParEpt = cout.map((c, i) => (ept[i] > 0 ? c / ept[i] : null));

  // Filet réel / futur : index du 1er mois ouvert (post-clôture) dans l'année affichée.
  const sepIdx = (() => {
    const lcp = activeTenant?.last_closed_period;
    if (!lcp) return -1;
    const [ly, lm] = String(lcp).split('-').map(Number);
    if (!ly || !lm) return -1;
    let foYear = ly, foIdx = lm;            // mois ouvert = mois suivant le dernier clos
    if (lm >= 12) { foYear = ly + 1; foIdx = 0; }
    if (N !== foYear) return -1;            // année entièrement réelle ou entièrement future
    return foIdx >= 1 ? foIdx : -1;
  })();

  const fmt = (v, blank) => (blank && Math.round(v) === 0 ? '—' : formatMoney(v, unit, 0));
  const pct = (v, base) => (Math.round(base) === 0 ? '—' : `${((v / base) * 100).toFixed(1)}%`);
  const PctRow = ({ label, arr, base }) => {
    const tot = arr.reduce((a, b) => a + b, 0);
    const totBase = base.reduce((a, b) => a + b, 0);
    return (
      <tr className="scs__row scs__row--pct">
        <td className="scs__lbl">{label}</td>
        {arr.map((v, i) => (
          <td key={i} className={`scs__cell${i === sepIdx ? ' scs__cell--sep' : ''}`}>{pct(v, base[i])}</td>
        ))}
        <td className="scs__cell scs__cell--total">{pct(tot, totBase)}</td>
      </tr>
    );
  };
  const Row = ({ label, arr, variant, blank }) => {
    const total = arr.reduce((a, b) => a + b, 0);
    return (
      <tr className={`scs__row${variant ? ` scs__row--${variant}` : ''}`}>
        <td className="scs__lbl">{label}</td>
        {arr.map((v, i) => (
          <td key={i} className={`scs__cell${i === sepIdx ? ' scs__cell--sep' : ''}`}>{fmt(v, blank)}</td>
        ))}
        <td className="scs__cell scs__cell--total">{fmt(total, blank)}</td>
      </tr>
    );
  };

  /* Poser un poste vacant / une réduction depuis le graphique : le mouvement
     part au 1er du mois cliqué, puis on recharge les deux séries. Le montant
     par défaut et la nature restent ceux du Kanban de saisie. */
  return (
    <main className={`hr-page${embedded ? ' is-embedded' : ''}`}>
      {!embedded && (
        <PageHeader
          title="Plan masse salariale"
        />
      )}

      {loading && <div className="hr-page__loading">Loading…</div>}
      {!loading && !plan.hasData && (
        <p className="hr-page__empty">Aucune charge sociale saisie pour {N}. Saisis les taux dans Input People → Charges sociales.</p>
      )}
      {!loading && plan.hasData && (
        /* Entête standard de carte (2026-09-01) : titre + contexte à gauche,
           filtres derrière l'entonnoir, recherche en dernier. */
        <CardShell
          flush
          title={<>{p('Salaires et charges sociales')} <small>· {N} · {p('brut planifié × taux')} · {chfLbl}</small></>}
          filters={(
            <>
              <FilterIcon />
              <SegmentedToggle
                compact
                label={p('Statut')} value={statusFilter} onChange={setStatusFilter}
                options={[
                  { value: 'all', label: p('Tous') },
                  { value: 'active', label: p('Actifs'), tone: 'alert' },
                ]}
              />
              <FilterCount count={(statusFilter === 'active' ? 1 : 0) + (search.trim() ? 1 : 0)}
                           onClear={() => { setStatusFilter('all'); setSearch(''); }} />
            </>
          )}
          search={(
            <SearchField value={search} onChange={setSearch}
                         placeholder={p('Rechercher…')} />
          )}
          filterCount={(statusFilter === 'active' ? 1 : 0) + (search.trim() ? 1 : 0)}
          collapsible storageKey="hr-payroll-plan"
        >
          <div className="scs__scroll">
            <table className="scs__table">
              <thead>
                <tr>
                  <th className="scs__lbl scs__lbl--head"> </th>
                  {months.map((m, i) => (
                    <th key={i} className={`scs__mh${i === sepIdx ? ' scs__mh--sep' : ''}`}>{m.label}</th>
                  ))}
                  <th className="scs__mh scs__mh--total">Total</th>
                </tr>
              </thead>
              <tbody>
                <Row label="Salaires bruts" arr={gross} variant="brut" />

                <tr className="scs__sec"><td className="scs__lbl" colSpan={14}>{t('phrases:p_0834336deb')}</td></tr>
                {charges.filter((c) => c.hasEmpl).map((c) => (
                  <Row key={`empl-${c.libelle}`} label={c.libelle} arr={c.empl} variant="cost" blank />
                ))}
                <Row label={t('phrases:p_e37463cb94')} arr={chargesSalarie} variant="subcost" />
                <PctRow label="% des salaires bruts" arr={chargesSalarie} base={gross} />
                <Row label="= Salaires nets" arr={net} variant="strong" />

                <tr className="scs__sec"><td className="scs__lbl" colSpan={14}>Charges employeur</td></tr>
                {charges.filter((c) => c.hasEmpr).map((c) => (
                  <Row key={`empr-${c.libelle}`} label={c.libelle} arr={c.empr} variant="empr" blank />
                ))}
                <Row label="Sous-total charges employeur" arr={chargesEmployeur} variant="subempr" />
                <PctRow label="% des salaires bruts" arr={chargesEmployeur} base={gross} />
                <Row label={p('= Coût employeur')} arr={cout} variant="strong" />

                <tr className="scs__sec"><td className="scs__lbl" colSpan={14}>Sous-total charges sociales (salarié + employeur)</td></tr>
                <Row label="Total charges sociales" arr={sub} variant="brut" />

                <tr><td colSpan={14} style={{ height: 16 }} /></tr>
                <tr className="scs__row">
                  <td className="scs__lbl">EPT</td>
                  {ept.map((v, i) => (
                    <td key={i} className={`scs__cell${i === sepIdx ? ' scs__cell--sep' : ''}`}>
                      {v.toLocaleString('fr-CH', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}
                    </td>
                  ))}
                  <td className="scs__cell scs__cell--total">
                    {(ept.reduce((a, b) => a + b, 0) / 12).toLocaleString('fr-CH', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}
                  </td>
                </tr>
                <tr className="scs__row">
                  <td className="scs__lbl">{t('phrases:p_21c92dab87')}</td>
                  {coutParEpt.map((v, i) => (
                    <td key={i} className={`scs__cell${i === sepIdx ? ' scs__cell--sep' : ''}`}>
                      {v != null ? fmt(v) : '—'}
                    </td>
                  ))}
                  <td className="scs__cell scs__cell--total">
                    {ept.reduce((a, b) => a + b, 0) > 0
                      ? fmt(cout.reduce((a, b) => a + b, 0) / ept.reduce((a, b) => a + b, 0))
                      : '—'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardShell>
      )}
    </main>
  );
}
